import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output
} from '@angular/core';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { lastValueFrom, map } from 'rxjs';
import { Delivery } from 'src/app/models';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { HlCurrencyPipe } from 'src/app/shared/pipes/hl-currency.pipe';
import { CustomerService } from 'src/app/shared/services/customer.service';

@Component({
  selector: 'app-previous-delivery-select',
  standalone: true,
  imports: [
    MatSelectModule,
    HlCurrencyPipe,
    AsyncPipe,
    MatFormFieldModule,
    MatOptionModule,
    TestIdDirective
  ],
  template: ` <mat-form-field class="w-full" testId="previous-delivery-select">
    <mat-label>Previous delivery</mat-label>
    <mat-select
      mat-form-field
      (selectionChange)="previousDeliverySelected.emit($event.value)"
    >
      @if (previousDeliveries.isLoading()) {
        <mat-option value="loading">Loading...</mat-option>
      } @else {
        @if (previousDeliveries.isError()) {
          <mat-option value="error">Error</mat-option>
        } @else {
          @if (
            previousDeliveries.isSuccess() &&
            previousDeliveries.data().length > 0
          ) {
            @for (delivery of previousDeliveries.data(); track delivery.id) {
              <mat-option [value]="delivery">
                {{ delivery.location }} ({{
                  delivery.charge
                    | hlCurrency: currency() : currencyRate() : false
                    | async
                }})
              </mat-option>
            }
          }
        }
      }
    </mat-select>
  </mat-form-field>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CustomerService]
})
export class PreviousDeliverySelectComponent {
  customerId = input<number>();
  customerService = inject(CustomerService);
  previousDeliveries = injectQuery(() => {
    return {
      queryKey: ['previous-deliveries', this.customerId()],
      queryFn: () =>
        lastValueFrom(
          this.customerService.getPreviousDeliveries(this.customerId()).pipe(
            map((deliveries) =>
              deliveries.reduce((acc: Delivery[], delivery) => {
                if (
                  acc.find(
                    (del) =>
                      (delivery.google_place_id &&
                        del.google_place_id === delivery.google_place_id) ||
                      del.location === delivery.location
                  ) === undefined
                ) {
                  acc.push(delivery);
                }
                return acc;
              }, [] as Delivery[])
            )
          )
        ),
      enabled: !!this.customerId()
    };
  });
  currency = input<string>();
  currencyRate = input<number>();

  previousDeliverySelected = output<Delivery>();
}
